import React, { useState, Fragment, useEffect, useMemo, useCallback } from 'react';
import _ from 'lodash';

import { getTutorials } from '../../../store/tutorials';
import { getCoverData, initialFVICoverData } from '../../../store/coverData';
import { getReportDetails, setItemsPerPage, setLastPhotoNumber } from '../../../store/reportDetails';
import { useAppDispatch, useAppSelector } from '../../../store/configureStore';

import { DEFAULT_REPORT_TITLE, TABLE_ITEM_COUNT_LIMITS, tutorialConstants } from '../../../utils/constants';
import { PDFImageProps, User } from '../../../utils/models';
import { getCurrentUser } from '../../../services/authService';
import { reArrangePhotoRecord, validateWorkOrderNumber } from '../../../utils/utilities';
import { PMIResult } from '../../forms/custom-pmi/pmiResultForm';
import { PMIEquipment } from '../../forms/custom-pmi/pmiEquipmentForm';

import Button from '../../themed/button';
import Conditional from '../../common/conditional';
import CustomCoverForm from '../../forms/custom-fvi/coverForm';
import CustomPMIReport from './';
import Drawing from '../../forms/custom-lpt/drawing';
import Elevation from '../../themed/instruction-modals/elevation';
import ExecutiveSummaryForm from '../../forms/visual-inspection/executiveSummaryForm';
import LoadingBadge from '../../themed/loading-badge';
import Modal from '../../themed/modal';
import FVICoverData from '../../themed/fvi-cover-data';
import MUTPhotograpicRecord from '../../forms/mut/mutPhotographicRecord';
import PMIResultItems from './pmiResultItems';
import PMIEquipmentItems from './pmiEquipmentItems';
import PMIResultElements from './pmiResultElements';
import ReportTitleForm from '../../forms/custom-fvi/reportTitleForm';
import SyncTime from '../../blocks/sync-time';
import WarningNotice from '../custom-visual-inspection/warning-notice';
import Zippy from '../../themed/zippy';

import useAutoSaveReport from '../../../hooks/useAutoSave';
import useInspectedItemIDUpdate from '../../../hooks/useInspectedItemIDUpdate';
import useProjectDetails from '../../../hooks/useProjectDetails';

import config from '../../../utils/config';
import toastService from '../../../services/toastService';

export interface PMIResultData {
	elements: string[];
	data: PMIResult[];
}

const CreateCustomPMIReport: React.FC = () => {
	const { fviCoverData } = useAppSelector(getCoverData);
	const { isSaving } = useAppSelector(getReportDetails);

	const [pmiResultData, setPMIResultData] = useState<PMIResultData>({ elements: [], data: [] });
	const [previewReport, setPreviewReport] = useState<boolean>(false);
	const [reportTitle, setReportTitle] = useState(DEFAULT_REPORT_TITLE);
	const [coverData, setCoverData] = useState(initialFVICoverData);
	const [executiveSummary, setExecutiveSummary] = useState('');
	const [drawing, setDrawing] = useState<PDFImageProps[]>([]);
	const [pmiEquipmentItems, setPMIEquipmentItems] = useState<PMIEquipment[]>([]);
	const [photographicData, setPhotographicData] = useState<PDFImageProps[]>([]);

	const { projectDetails, error } = useProjectDetails();
	const { activeTutorial } = useAppSelector(getTutorials);
	const { role } = getCurrentUser() as User;

	const reportDetails = useMemo(() => {
		return {
			reportTitle,
			coverData,
			drawing,
			executiveSummary,
			pmiResultData,
			pmiEquipmentItems,
			photographicData,
			projectDetails,
		};
	}, [projectDetails, coverData, drawing, executiveSummary, pmiEquipmentItems, pmiResultData, photographicData, reportTitle]);

	const autoSave = useAutoSaveReport(reportDetails);
	const dispatch = useAppDispatch();

	const validationMessage = useMemo(() => {
		if (!validateWorkOrderNumber(coverData.workOrderNumber))
			return 'Enter a valid work order number!';

		if (!executiveSummary) {
			return 'Executive summary missing!';
		}

		if (pmiResultData.data.length === 0) {
			return 'Please provide at least one PMI result data item';
		}
		
		if (pmiEquipmentItems.length === 0) {
			return 'Please provide at least one PMI equipment';
		}

		if (drawing.length === 0) {
			return 'Please upload General Drawing!';
		}
		
		if (photographicData.length === 0) {
			return 'Please upload photographic report!';
		}
		
		return '';
	}, [coverData, drawing, executiveSummary, pmiResultData, pmiEquipmentItems, photographicData])

	const togglePreviewReport = useCallback((): void => {
		if (config.enableReportCompletionBeforePublish) {
			if (validationMessage) return toastService.error(validationMessage);
		}
		
		setPreviewReport((p: boolean) => !p);
	}, [validationMessage]);

	const handleSaveAndPreview = useCallback(() => {
		autoSave.saveReport();
		togglePreviewReport();
	}, [autoSave, togglePreviewReport]);

	const handleSavePMIElements = useCallback((elements: string[]) => {
		setPMIResultData((previous) => ({
			elements,
			data: previous.data,
		}));
	}, []);
	
	const handleSavePMIResults = useCallback((results: PMIResult[]) => {
		setPMIResultData((previous) => ({
			elements: previous.elements,
			data: results,
		}));
	}, []);

	const handleExtractPMIDataFromResults = (results: PMIResult[]) => {
		const elements = _.omit(results[0], ['itemDescription', 'identifiedMaterialSpec', 'result']);

		setPMIResultData({
			elements: _.keys(elements),
			data: results,
		});
	};
	
	useInspectedItemIDUpdate(pmiResultData.data.map((result) => result.itemDescription));
	
	useEffect(() => {
		function initializeCache() {
			const cachedReport = autoSave.oldReportData as (typeof reportDetails & { pmiResultItems: PMIResult[]; }) | null;
			
			if (!cachedReport) {
				if (fviCoverData.coverData.length > 0) setCoverData(fviCoverData);
				
				return;
			}

			if (cachedReport.pmiEquipmentItems) {
				setPMIEquipmentItems(cachedReport.pmiEquipmentItems);
			}

			if (cachedReport.drawing) {
				setDrawing(cachedReport.drawing);
			}
			
			if (cachedReport.pmiResultItems) {
				handleExtractPMIDataFromResults(cachedReport.pmiResultItems);
			}

			if (cachedReport.pmiResultData) {
				setPMIResultData(cachedReport.pmiResultData);
			}

			setReportTitle(cachedReport.reportTitle);
			setCoverData(cachedReport.coverData?.coverData?.length > 0 ? cachedReport.coverData : fviCoverData);
			setExecutiveSummary(cachedReport.executiveSummary);
			setPhotographicData(reArrangePhotoRecord(cachedReport.photographicData));

			dispatch(setLastPhotoNumber(cachedReport.photographicData.length));
		}

		initializeCache();
	}, [autoSave.oldReportData, dispatch, fviCoverData]);

	useEffect(() => {
		dispatch(setItemsPerPage(TABLE_ITEM_COUNT_LIMITS.PMI));
	}, [dispatch]);

	return (
		<Fragment>
			<Modal isVisible={previewReport} onClose={togglePreviewReport}>
				<CustomPMIReport {...reportDetails} />
			</Modal>

			<div className="mb-4">
				<SyncTime lastSynced={autoSave.lastSaved} />
			</div>

			{projectDetails && (
				<FVICoverData
					coverDataId={config.coverDataTemplates.pmi}
					isVisible={projectDetails.inspectionTypeId === config.supportedTemplates.pmi && role.approveReports}
					subtitle={`Update the Cover Data information. This is the default data used on all ${projectDetails.organisationName} ${projectDetails.inspectionTypeName} Reports`}
				/>
			)}

			<Conditional isVisible={validationMessage.length > 0}>
				<WarningNotice title="Report Incomplete" body={validationMessage} />
			</Conditional>

			<Zippy headerTitle="Report Title" isValid={reportTitle.length > 0}>
				<ReportTitleForm projectTitle={projectDetails?.projectTitle} onAdd={setReportTitle} />
			</Zippy>

			<Zippy headerTitle="Cover Sheet" isValid={coverData.coverData.length > 0}>
				<CustomCoverForm initialData={coverData} onAddCoverForm={setCoverData} />
			</Zippy>
			
			<Zippy headerTitle="Executive Summary" isValid={executiveSummary.length > 0}>
				<ExecutiveSummaryForm onProceed={setExecutiveSummary} initialSummary={executiveSummary} />
			</Zippy>

			<Zippy headerTitle="PMI Result" isValid={pmiResultData.data.length > 0}>
				<PMIResultElements elements={pmiResultData.elements} onDataChange={handleSavePMIElements} />

				<Conditional isVisible={pmiResultData.elements.length > 0}>
					<PMIResultItems elements={pmiResultData.elements} pmiResultItems={pmiResultData.data} onChangePMIResultItems={handleSavePMIResults} />
				</Conditional>
			</Zippy>
			
			<Zippy headerTitle="PMI Equipment" isValid={pmiEquipmentItems.length > 0}>
				<PMIEquipmentItems pmiEquipmentItems={pmiEquipmentItems} onChangePMIEquipmentItems={setPMIEquipmentItems} />
			</Zippy>

			<Zippy headerTitle="Drawing" isValid={drawing.length > 0}>
				<Drawing initialDrawing={drawing} onAdd={setDrawing} />
			</Zippy>

			<Zippy headerTitle="Photographic Report" isValid={photographicData.length > 0}>
				<MUTPhotograpicRecord initialImages={photographicData} onProceed={setPhotographicData} />
			</Zippy>

			<div className="d-flex justify-content-between align-items-center">
				<div>
					<Conditional isVisible={autoSave.isProcessing}>
						<LoadingBadge message="Saving" />
					</Conditional>
				</div>

				<Elevation isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}>
					<Button
						label="Save and Preview Report"
						disabled={error || autoSave.isProcessing || isSaving}
						onClick={handleSaveAndPreview}
					/>
				</Elevation>
			</div>
		</Fragment>
	);
};

export default CreateCustomPMIReport;
